import { memo, useMemo } from 'react'
import { LinkTypeSchema } from 'cms/schemaTypes/generalSchemas/linkType'
import { PortableText, PortableTextProps } from 'next-sanity'

import {
  CenterDecorator,
  ColorDecorator,
  formatAndSerialize,
  formatText,
  LeftDecorator,
  LinkDecorator,
  renderAlignedChildren,
  RichTextAlignmentValue,
  RightDecorator,
  StyledOlList,
  StyledStrong,
  StyledUlList,
  TextStyleBase,
  TextStyleBlockquote,
  TextStyleH1,
  TextStyleH2,
  TextStyleH3,
  TextStyleH4,
  TextStyleH5,
  TextStyleH6,
  TextStyleLarge,
  TextStyleMicro,
  TextStyleNano,
  TextStyleSmall
} from './shared'

const RichText: React.NamedExoticComponent<PortableTextProps> = memo(
  function RichTextRenderer({
    components: _,
    ...props
  }: PortableTextProps): React.JSX.Element {
    const blockParsers = useMemo(
      () => ({
        h1: formatAndSerialize(TextStyleH1),
        h2: formatAndSerialize(TextStyleH2),
        h3: formatAndSerialize(TextStyleH3),
        h4: formatAndSerialize(TextStyleH4),
        h5: formatAndSerialize(TextStyleH5),
        h6: formatAndSerialize(TextStyleH6),
        nano: formatAndSerialize(TextStyleNano),
        micro: formatAndSerialize(TextStyleMicro),
        small: formatAndSerialize(TextStyleSmall),
        normal: formatAndSerialize(TextStyleBase),
        large: formatAndSerialize(TextStyleLarge),
        blockquote: formatAndSerialize(TextStyleBlockquote)
      }),
      []
    )

    return (
      <PortableText
        components={{
          marks: {
            alignment: ({ children, value }) =>
              renderAlignedChildren(
                children,
                value as RichTextAlignmentValue | undefined
              ),
            left: ({ children }) => LeftDecorator({ children }),
            right: ({ children }) => RightDecorator({ children }),
            center: ({ children }) => CenterDecorator({ children }),
            color: ({ children, value }) =>
              ColorDecorator({
                children,
                value: value as {
                  color?: { hex?: string }
                  colorReference?: { color?: string }
                }
              }),
            link: ({ children, value }) =>
              LinkDecorator({
                children,
                value: value as { link?: LinkTypeSchema }
              }),
            strong: ({ children }) => <StyledStrong>{children}</StyledStrong>
          },
          block: {
            h1: ({ children }) => blockParsers.h1({ children }),
            h2: ({ children }) => blockParsers.h2({ children }),
            h3: ({ children }) => blockParsers.h3({ children }),
            h4: ({ children }) => blockParsers.h4({ children }),
            h5: ({ children }) => blockParsers.h5({ children }),
            h6: ({ children }) => blockParsers.h6({ children }),
            nano: ({ children }) => blockParsers.nano({ children }),
            micro: ({ children }) => blockParsers.micro({ children }),
            small: ({ children }) => blockParsers.small({ children }),
            normal: ({ children }) => blockParsers.normal({ children }),
            large: ({ children }) => blockParsers.large({ children }),
            blockquote: ({ children }) => blockParsers.blockquote({ children })
          },
          list: {
            bullet: ({ children }) => (
              <StyledUlList>{formatText(children)}</StyledUlList>
            ),
            number: ({ children }) => (
              <StyledOlList>{formatText(children)}</StyledOlList>
            )
          }
        }}
        {...props}
      />
    )
  }
)

export default RichText
